import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import 'highlight.js/styles/github-dark-dimmed.css';

import { Button } from '@/components/ui/Button';

const SKILL_SLUG = 'llmcontext-skills';
const SKILL_URL = `/skills/${SKILL_SLUG}.md`;
const CLAUDE_SKILL_PATH = `.claude/skills/${SKILL_SLUG}/SKILL.md`;
const GITHUB_SKILL_PATH = `.github/skills/${SKILL_SLUG}/SKILL.md`;

type SkillPreview = {
  name: string | null;
  description: string | null;
  body: string;
};

function parseSkillPreview(markdown: string): SkillPreview {
  const match = markdown.match(/^---\n([\s\S]*?)\n---\n*/);
  if (!match) {
    return { name: null, description: null, body: markdown };
  }

  const field = (key: string): string | null => {
    const m = new RegExp(`^${key}:\\s*(.+)$`, 'm').exec(match[1]);
    return m ? m[1].trim() : null;
  };

  return {
    name: field('name'),
    description: field('description'),
    body: markdown.slice(match[0].length).trim(),
  };
}

function SnippetBlock({ title, code }: { title: string; code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore clipboard failures */
    }
  };

  return (
    <div className="overflow-hidden border border-rule bg-code-bg">
      <div className="barhead justify-between normal-case tracking-normal">
        <span className="truncate text-xs font-medium text-accent">{title}</span>
        <button type="button" onClick={handleCopy} className="text-2xs text-dim hover:text-ink">
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="px-4 py-3 font-mono text-xs leading-relaxed text-link whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
    </div>
  );
}

/**
 * The agent-skill block of the Get started page (also the /skills deep-link
 * target, which scrolls here). Owns the SKILL.md viewer and the manual
 * install fallback; the install command and client list live in the parent
 * page's One-line install section, so they are deliberately not repeated.
 * The full skill body stays collapsed by default — the section leads with
 * what the skill does, how to get it, and where it lives.
 */
export function AgentSkillSection() {
  const [content, setContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const preview = useMemo(() => (content ? parseSkillPreview(content) : null), [content]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(SKILL_URL);
        if (!res.ok) throw new Error(`Failed to load skill (${res.status})`);
        const text = await res.text();
        if (!cancelled) setContent(text);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load skill.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const copy = async () => {
    if (!content) return;
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked — user can still download */
    }
  };

  const download = () => {
    if (!content) return;
    const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'SKILL.md';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <section id="agent-skill" className="scroll-mt-20 space-y-4">
      <div>
        <p className="page-eyebrow">Step 2 · Teach the agent</p>
        <h2 className="mt-2 text-lg font-semibold text-ink">
          Teach the agent when to query llmcontext
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-dim">
          The skill teaches your coding agent to use llmcontext as a shared evidence concierge:
          resolve the exact package version, ask one focused information question, and apply the
          retrieved evidence locally instead of repeating package discovery in every session. Step 1
          installs it automatically; this section is for inspection and manual setup.
        </p>
      </div>

      <div className="sheet">
        {/* Header row: identity + actions, always visible */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-rule px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink">
              SKILL.md
              {preview?.name && (
                <span className="ml-2 font-mono text-xs font-normal text-dim">{preview.name}</span>
              )}
            </h3>
            {error ? (
              <p className="mt-1 text-xs text-del">{error}</p>
            ) : preview?.description ? (
              <p className="mt-1 max-w-2xl text-xs leading-relaxed text-dim">
                {preview.description}
              </p>
            ) : (
              <p className="mt-1 text-xs text-dim">
                {content === null ? 'Loading…' : 'Published skill file, copied by the installer.'}
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button size="sm" variant="secondary" onClick={copy} disabled={!content}>
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Button size="sm" onClick={download} disabled={!content}>
              Download
            </Button>
          </div>
        </div>

        {/* Manual install paths, compact two-up */}
        <div className="grid gap-3 border-b border-rule bg-bg p-4 sm:grid-cols-2">
          <SnippetBlock title="Claude Code skill path" code={CLAUDE_SKILL_PATH} />
          <SnippetBlock title="Example custom skill path" code={GITHUB_SKILL_PATH} />
        </div>

        {/* Full body, collapsed by default */}
        <div className="px-4 py-3">
          <button
            type="button"
            onClick={() => setShowFull((v) => !v)}
            disabled={!content}
            className="text-sm font-medium text-accent hover:underline disabled:opacity-50"
          >
            {showFull ? 'Hide the full skill ↑' : 'Read the full skill ↓'}
          </button>
          {showFull && content && (
            <div className="lc-md-preview mt-4 max-h-[60vh] overflow-auto border-t border-rule pt-4">
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                {preview?.body ?? content}
              </ReactMarkdown>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
